import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, Link } from 'react-router-dom'
import { FaArrowLeft, FaUserCog } from 'react-icons/fa'
import api from '../../../libs/axios'
import { useAuth } from '../../../provider/auth.provider'

interface TeamMember { id: number; first_name: string; last_name: string; username: string }
interface Site {
  id: number
  site_name: string
  reference_number: string
  survey_workflow_status: string
  site_priority: string
  project?: { project_name: string }
  state_circle?: { state_name: string }
  site_engineer?: TeamMember
  report_designer?: TeamMember
}

const InfoRow = ({ label, value }: { label: string; value?: string | null }) => (
  <div className="flex flex-col sm:flex-row sm:items-center py-2.5 border-b border-gray-50 last:border-0">
    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide w-48 flex-shrink-0">{label}</dt>
    <dd className="text-sm text-gray-800 mt-0.5 sm:mt-0">{value ?? '—'}</dd>
  </div>
)

export default function VendorPortalSiteView() {
  const { id } = useParams<{ id: string }>()
  const { hasPermission } = useAuth()
  const canReassign = hasPermission('VendorPortal', 'manage_team')

  const [site, setSite] = useState<Site | null>(null)
  const [loading, setLoading] = useState(true)

  // Reassign state
  const [engineers, setEngineers] = useState<TeamMember[]>([])
  const [reportMakers, setReportMakers] = useState<TeamMember[]>([])
  const [reassigning, setReassigning] = useState<'engineer' | 'report_maker' | null>(null)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    api.get(`/vendor-portal/sites/${id}`)
      .then(r => setSite(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false))

    if (canReassign) {
      api.get('/vendor-portal/team', { params: { role_id: 7 } }).then(r => setEngineers(r.data.data ?? []))
      api.get('/vendor-portal/team', { params: { role_id: 9 } }).then(r => setReportMakers(r.data.data ?? []))
    }
  }, [id, canReassign])

  const handleReassign = async () => {
    if (!selectedUserId || !reassigning) return
    setSaving(true)
    try {
      const endpoint = reassigning === 'engineer'
        ? `/vendor-portal/sites/${id}/reassign-engineer`
        : `/vendor-portal/sites/${id}/reassign-report-maker`
      const res = await api.patch(endpoint, { new_user_id: Number(selectedUserId) })
      setSite(prev => prev ? { ...prev, ...res.data.data } : prev)
      setReassigning(null)
      setSelectedUserId('')
    } catch {
      // toast handled by axios interceptor
    } finally {
      setSaving(false)
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <div className="animate-spin rounded-full border-b-2 border-blue-600 h-8 w-8" />
    </div>
  )

  if (!site) return (
    <div className="text-center py-12 text-gray-500">Site not found</div>
  )

  const fullName = (m?: TeamMember) => m ? `${m.first_name} ${m.last_name} (${m.username})` : '—'

  return (
    <>
      <Helmet><title>Vendor Portal – {site.site_name ?? 'Site'}</title></Helmet>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <Link to="/vendor-portal/sites" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
            <FaArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <h1 className="text-xl font-bold text-gray-900">{site.site_name}</h1>
        </div>

        {/* Site Details */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Site Details</h2>
          <dl>
            <InfoRow label="Reference" value={site.reference_number} />
            <InfoRow label="Project" value={site.project?.project_name} />
            <InfoRow label="Circle" value={site.state_circle?.state_name} />
            <InfoRow label="Priority" value={site.site_priority} />
            <InfoRow label="Status" value={site.survey_workflow_status?.replace(/_/g, ' ')} />
          </dl>
        </div>

        {/* Team Assignments */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Team Assignments</h2>

          {/* Site Engineer */}
          <div className="flex items-center justify-between py-3 border-b border-gray-50">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">Site Engineer</p>
              <p className="text-sm text-gray-800">{fullName(site.site_engineer)}</p>
            </div>
            {canReassign && (
              <button
                onClick={() => { setReassigning('engineer'); setSelectedUserId('') }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
              >
                <FaUserCog className="w-3.5 h-3.5" /> Reassign
              </button>
            )}
          </div>

          {/* Report Maker */}
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">Report Maker</p>
              <p className="text-sm text-gray-800">{fullName(site.report_designer)}</p>
            </div>
            {canReassign && (
              <button
                onClick={() => { setReassigning('report_maker'); setSelectedUserId('') }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
              >
                <FaUserCog className="w-3.5 h-3.5" /> Reassign
              </button>
            )}
          </div>
        </div>

        {/* Reassign Modal */}
        {reassigning && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Reassign {reassigning === 'engineer' ? 'Site Engineer' : 'Report Maker'}
              </h3>
              <select
                value={selectedUserId}
                onChange={e => setSelectedUserId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              >
                <option value="">Select team member...</option>
                {(reassigning === 'engineer' ? engineers : reportMakers).map(m => (
                  <option key={m.id} value={m.id}>
                    {m.first_name} {m.last_name} ({m.username})
                  </option>
                ))}
              </select>
              <div className="flex gap-3">
                <button
                  onClick={() => setReassigning(null)}
                  className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReassign}
                  disabled={!selectedUserId || saving}
                  className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                >
                  {saving ? 'Saving...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
